import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { complaintmodel } from "../../models/complaints/complaint-model";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { Tag } from "primereact/tag";
import ComplaintInvoice from "../ComplainstList/ComplaintInvoice";
import { ComplaintInvoiceModel } from "../../models/complaints/complaint-model";
import ComplaintService from "../../services/complaint/ComplaintService";
import ComplaintActions from "./ComplaintActions";
import { complaintmodel2 } from "../../models/complaints/complaint-model";
import UpdateServiceComplaintDataTable from "./UpdateServiceComplaintDataTable";
import UpdateProductComplaintDataTable from "./UpdateProductComplaintDataTable";
import UpdateCustServiceComplaintDataTable from "./UpdateCustServiceComplaintDataTable";
import UpdateCustProductComplaintDataTable from "./UpdateCustProductComplaintDataTable";
// import UpdateCustServiceComplaintDataTable from "./UpdateCustServiceComplaintDataTable";

interface DataTableProps {
  data: complaintmodel[];
  handlepagechange: (event: any) => void;
  updatedData: (updatedComplaint: complaintmodel2) => void;
  pageNo: number;
  pageSize: number;
  complaintList: (list: complaintmodel[]) => void;
  totalRecordsCount: number;
  complaintType: "product" | "service";
}

const CustomerComplaintDataTable: React.FC<DataTableProps> = ({
  data,
  handlepagechange,
  pageNo,
  pageSize,
  totalRecordsCount,
  complaintType = "product",
  updatedData,
}) => {
  const { t } = useTranslation();
  const toast = useRef<Toast>(null);
  const [showActionsModal, setShowActionsModal] = useState(false);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<ComplaintInvoiceModel[] | null>(null);
  const [selectedComplaint, setSelectedComplaint] = useState<complaintmodel | null>(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [containerType,setContainerType] = useState<string>("Complaints");

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getSeverityTag = (severity: string) => {
    const map: any = {
      low: "info",
      medium: "warning",
      high: "danger",
      critical: "danger",
    };
    return <Tag value={severity} severity={map[severity?.toLowerCase()] || "info"} />;
  };

  const phoneTemplate = (rowData: any) => rowData.mobile || "-";

  const actionTemplate = (rowData: any) => (
    <Button
      icon="pi pi-pencil"
      className="p-button-rounded p-button-text"
      onClick={() => {
        setSelectedComplaint(rowData); 
        setShowActionsModal(true);
      }}
      tooltip="Add Action"
    />
  );

  const editTemplate = (rowData: complaintmodel) => (
    <Button
      icon="pi pi-pencil"
      className="p-button-rounded p-button-text p-button-success"
      onClick={() => {
        rowData.containerType = containerType;
        setSelectedComplaint(rowData);
        setShowUpdateDialog(true);
      }}
      tooltip="Edit Complaint"
    />
  );

  const handleActionSuccess = () => {
    setShowActionsModal(false);
    showToast("Update Complaint", "Complaint updated successfully", "success");
  };

  const showToast = (label: string, message: string, severity: "success" | "error" | "info" | "warn") => {
    toast.current?.show({ severity, summary: label, detail: message, life: 3000 });
  };

  const handleInvoiceClick = async (rowData: complaintmodel) => {
    if (!rowData.invoiceNo) {
      showToast("Error", "No invoice number available", "error");
      return;
    }

    setLoadingInvoice(true);
    try {
      const invoiceDetails = await ComplaintService.InvoiceNumber(Number(rowData.invoiceNo));
      if (invoiceDetails && Array.isArray(invoiceDetails) && invoiceDetails.length > 0) {
        setSelectedInvoice(invoiceDetails); 
        setShowInvoiceModal(true);
      } else {
        showToast("Error", "No invoice data found", "error");
      }
    } catch (error) {
      console.error("Error fetching invoice:", error);
      showToast("Error", "Failed to load invoice details", "error");
    } finally {
      setLoadingInvoice(false);
    }
  };

  const renderProductTable = () => (
    <DataTable
      value={data}
      paginator
      rowsPerPageOptions={[5, 10, 25, 50, 100]} 
      first={(pageNo - 1) * pageSize}
      totalRecords={totalRecordsCount}
      rows={pageSize}
      stripedRows
      onPage={handlepagechange}
      paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
      currentPageReportTemplate="{first} to {last} of {totalRecords}"
      loading={loadingInvoice}
      emptyMessage="No complaints found"
    >
      <Column field="id" header="Complaint ID" sortable/>
      <Column field="createdDate" header="Complaint Date" body={(row) => formatDate(row.createdDate)} sortable/>
      <Column field="customerName" header="Customer Name" sortable />
      {/* <Column field="mobile" header="Primary Contact" body={phoneTemplate} /> */}
      
      <Column
        field="invoiceNo"
        header={t("Invoice No")}
        sortable
        body={(rowData: complaintmodel) => (
          <Button
            link
            label={rowData.invoiceNo?.toString()}
            onClick={() => handleInvoiceClick(rowData)}
            className="p-0 text-primary font-bold"
            disabled={!rowData.invoiceNo} 
          />
        )}
      />
      <Column field="productName" header="Product Name" sortable/>
      <Column field="description" header="Description" style={{ maxWidth: "300px", wordWrap: "break-word" }} />
      <Column field="modifiedDate" header="Modified Date" body={(row) => row.modifiedDate ? formatDate(row.modifiedDate) : "-"} sortable/>
      {/* <Column field="remarks" header="Remarks" /> */}
      {/* <Column field="severity" header="Severity" body={(row) => getSeverityTag(row.severity)} /> */}
      {/* <Column field="customerSupportAction" header="Support Action" /> */}
      {/* <Column field="createdByName" header="Created By" body={(row) => row.createdByName || "System"} /> */}
      {/* <Column field="modifiedByName" header="Modify By" body={(row) => row.modifiedByName || "System"} /> */}
      <Column header="Edit" body={editTemplate} />
      {/* <Column header="Action" body={actionTemplate} /> */}
    </DataTable>
  );

  const renderServiceTable = () => (
    <DataTable
      value={data}
      paginator
      rowsPerPageOptions={[5, 10, 25, 50, 100]} 
      first={(pageNo - 1) * pageSize}
      totalRecords={totalRecordsCount}
      rows={pageSize}
      stripedRows
      onPage={handlepagechange}
      paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
      currentPageReportTemplate="{first} to {last} of {totalRecords}"
      emptyMessage="No complaints found"
    >  
      <Column field="id" header="Complaint ID"  sortable/>    
      <Column field="createdDate" header="Complaint Date" body={(row) => formatDate(row.createdDate)} sortable/>
      <Column field="customerName" header="Customer Name" sortable/>
      <Column field="mobile" header="Primary Contact" body={phoneTemplate} sortable/>
      <Column field="description" header="Description" style={{ maxWidth: "300px", wordWrap: "break-word" }} />
      {/* <Column field="remarks" header="Remarks" />
      <Column field="severity" header="Severity" body={(row) => getSeverityTag(row.severity)} />
      <Column field="customerSupportAction" header="Support Action" /> */}
      {/* <Column field="createdByName" header="Created By" body={(row) => row.createdByName || "System"} /> */}
      {/* <Column field="modifiedByName" header="Modified By" body={(row) => row.modifiedByName || "-"} /> */}
      <Column field="modifiedDate" header="Modified Date" body={(row) => row.modifiedDate ? formatDate(row.modifiedDate) : "-"} sortable/>
      <Column header="Edit" body={editTemplate} />
      {/* <Column header="Action" body={actionTemplate} /> */}
    </DataTable>
  );

  return (
    <div className="col-12">
      <Toast ref={toast} />
      <div className="card border-noround p-2">
        {complaintType === "product" ? renderProductTable() : renderServiceTable()}

        <Dialog
          visible={showActionsModal}
          style={{ width: "65vw" }}
          onHide={() => setShowActionsModal(false)}
          modal
        >
          {selectedComplaint && (
            <ComplaintActions
              isUpdate={false}
              handleOnSuccess={handleActionSuccess}
              updateComplaintInfo={{
                ...selectedComplaint,
                productComplaintId: complaintType === "product" ? selectedComplaint.id : null,
                serviceComplaintId: complaintType === "service" ? selectedComplaint.id : null
              }}
              AddedData={null}
              selectedComplaint={selectedComplaint}
              complaintType={complaintType}
            />
          )}
        </Dialog>

        <Dialog
          visible={showUpdateDialog}
          style={{ width: "80vw" }}
          onHide={() => setShowUpdateDialog(false)}
          modal
        >
          {selectedComplaint && (
            complaintType === "product" ? (
              <UpdateCustProductComplaintDataTable
                isUpdate={true}
                updateComplaintInfo={selectedComplaint}
                handleOnSuccess={(updatedComplaint: complaintmodel2) => {
                  updatedData(updatedComplaint);
                  setShowUpdateDialog(false);
                  showToast("Success", "Product complaint updated successfully", "success");
                }}
              />
            ) : (
              <UpdateCustServiceComplaintDataTable
                isUpdate={true}
                updateComplaintInfo={selectedComplaint}
                handleOnSuccess={(updatedComplaint: complaintmodel2) => {
                  updatedData(updatedComplaint);
                  setShowUpdateDialog(false);
                  showToast("Success", "Service complaint updated successfully", "success");
                }}
              />
            )
          )}
        </Dialog>

        <Dialog
          visible={showInvoiceModal}
          style={{ width: "80vw" }}
          modal
          onHide={() => setShowInvoiceModal(false)}
          header="Invoice Details"
        >
          {loadingInvoice ? (
            <div>Loading invoice data...</div>
          ) : selectedInvoice ? (
            <ComplaintInvoice invoiceData={selectedInvoice} />
          ) : (
            <div>No invoice data available</div>
          )}
        </Dialog>
      </div>
    </div>
  );
};

export default CustomerComplaintDataTable;